import { Journey } from '@audibene/ta.utils-opportunity'
import { AxiosError } from 'axios'
import { AppointmentType, ToastSubType } from '../../../../constants'
import { Unavailabilities, UnavailabilitiesResponse } from '../../../../types'
import { HttpClientConfig } from '../../../useHttpClientConfig'
import { ToastNotifyFunction } from '../../../useNotification'
import { transformAppointmentResponseAppointment } from '../appointments'
import { apiVersion, service } from '../service'
import { FilterValue, applySearchParams, urlToPathnameAndSearch } from '../utils'

interface GetUnavailabilitiesRequest {
  appointment_type: AppointmentType
  duration_in_minutes?: number
  date_end: Date
  date_start: Date
  journey: Journey
  resource_ids: string[]
}

const pathname = `/api/${apiVersion}/unavailabilities`

const makeGetUnavailabilities =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async (request: GetUnavailabilitiesRequest): Promise<Unavailabilities> => {
    const url = new URL(pathname, baseUrl)

    applySearchParams(url, request as unknown as Record<string, FilterValue>)

    const pathnameAndSearch = urlToPathnameAndSearch(url)

    const { data: unavailabilitiesResponse } = await service(baseUrl)
      .get<UnavailabilitiesResponse>(pathnameAndSearch, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Our attempt to retrieve unavailabilities timed out. Please try again.`,
            type: ToastSubType.UNAVAILABILITIES_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    const unavailabilities: Unavailabilities = Object.entries(unavailabilitiesResponse).reduce((acc, [key, unavailabilityResponses]) => {
      const responses = unavailabilityResponses.map((unavailabilityResponse) => ({
        ...unavailabilityResponse,
        related_entity: unavailabilityResponse.related_entity?.appointment
          ? {
              ...unavailabilityResponse.related_entity,
              appointment: transformAppointmentResponseAppointment(unavailabilityResponse.related_entity.appointment),
            }
          : undefined,
      }))

      return {
        ...acc,
        [key]: responses,
      }
    }, {})

    return unavailabilities
  }

export { makeGetUnavailabilities }
export type { GetUnavailabilitiesRequest }
